/**
 * AnonymousContext — provides isAnonymous and isEmbedded flags to the
 * component tree when the app is loaded via the /demo/:projectId route.
 *
 * isAnonymous: true when no authenticated session exists (disables writes).
 * isEmbedded:  true when loaded inside an iframe OR with ?embed=1 query param
 *              (hides shell chrome for clean embed appearance).
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

interface AnonymousState {
  isAnonymous: boolean;
  isEmbedded: boolean;
}

const AnonymousContext = createContext<AnonymousState>({
  isAnonymous: false,
  isEmbedded: false,
});

export function useAnonymous(): AnonymousState {
  return useContext(AnonymousContext);
}

export function AnonymousProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();

  const state = useMemo<AnonymousState>(() => {
    const embedParam = searchParams.get("embed") === "1";
    const inIframe = typeof window !== "undefined" && window.self !== window.top;
    return {
      isAnonymous: true,
      isEmbedded: embedParam || inIframe,
    };
  }, [searchParams]);

  return (
    <AnonymousContext.Provider value={state}>
      {children}
    </AnonymousContext.Provider>
  );
}
